import React, { useState } from "react";
import { Field, FieldArray, getIn, useFormikContext } from "formik";
import { Icon } from "assets/Icon";
import Tooltip from "components/Tooltip";
import { BidderQuestionnaireHeader } from "./BidderQuestionnaireHeader";

export interface TableCell {
	id: string;
	value: string;
}

type TableRow = TableCell[];

interface TableTypeColumnData {
	id: string;
	header: string;
}

interface TableWithRowsQuestionnaireProps {
	name: string; // e.g. "question_answer.question-2294"
	data: TableTypeColumnData[];
	disabled?: boolean;
	headers: TableTypeColumnData[];
	isClientPreview: boolean;
	question?: string;
	required?: boolean;
}

export const TableWithRowsQuestionnaire: React.FC<
	TableWithRowsQuestionnaireProps
> = ({
	data,
	headers,
	isClientPreview,
	name,
	question,
	required = false,
	disabled = false,
}) => {
	const { errors, touched, setFieldTouched, values } = useFormikContext<any>();
	const [editCell, setEditCell] = useState<{
		colIndex: number;
		rowIndex: number;
	} | null>(null);

	const getCellPath = (rowIndex: number, colIndex: number) =>
		`${name}[${rowIndex}][${colIndex}].value`;

	const getCellError = (rowIndex: number, colIndex: number) => {
		const path = getCellPath(rowIndex, colIndex);
		const error = getIn(errors, path);
		const isTouched = getIn(touched, path);
		return isTouched && error ? error : undefined;
	};

	const handleSaveCell = (rowIndex: number, colIndex: number) => {
		const path = getCellPath(rowIndex, colIndex);
		setFieldTouched(path, true, true);
		setEditCell(null);
	};

	return (
		<FieldArray name={name}>
			{({ push, remove, form }) => {
				const rows: TableRow[] = getIn(form.values, name) || [];

				const addRow = () => {
					const newRow = headers?.map((col) => ({ id: col.id, value: "" }));
					push(newRow);

					// Mark all new cells as touched to trigger validation
					const rowIndex = getIn(values, name)?.length ?? 0;

					if (isClientPreview) {
						return;
					}

					// touch row to trigger validation
					newRow.forEach((_, colIndex) => {
						setFieldTouched(
							`${name}[${rowIndex}][${colIndex}].value`,
							true,
							true
						);
					});
				};

				// Initialize first row if empty
				if (rows.length === 0) {
					addRow();
				}

				return (
					<div className="w-full">
						<BidderQuestionnaireHeader
							question={question}
							required={required}
						/>
						<div className="mt-2 overflow-x-auto">
							<table className="w-full table-auto rounded-xl border border-solid border-[#EAECF0] bg-white">
								<thead>
									<tr>
										{headers?.map((col) => (
											<th
												key={col.id}
												className="border-b border-[#EAECF0] bg-[#F9FAFB] p-3 text-left font-roboto font-bold text-tertiary-600"
											>
												{col.header}
											</th>
										))}
										{!disabled && !isClientPreview && (
											<th className="w-20 border-b border-[#EAECF0] bg-[#F9FAFB] p-3"></th>
										)}
									</tr>
								</thead>
								<tbody>
									{rows.map((row, rowIndex) => (
										<tr
											key={rowIndex}
											className="border-b border-[#EAECF0]"
										>
											{row.map((cell, colIndex) => {
												const error = getCellError(rowIndex, colIndex);
												const handleKeyDown = (
													e: React.KeyboardEvent,
													rowIndex: number,
													colIndex: number
												) => {
													if (e.key === "Enter") {
														e.preventDefault();
														handleSaveCell(rowIndex, colIndex);

														// Move to next cell in row
														const nextCol = colIndex + 1;
														const nextRow =
															rowIndex + (nextCol >= row.length ? 1 : 0);
														const newColIndex =
															nextCol >= row.length ? 0 : nextCol;

														if (nextRow < rows.length) {
															setEditCell({
																rowIndex: nextRow,
																colIndex: newColIndex,
															});
														} else {
															addRow(); // add new row if at end
															setEditCell({
																rowIndex: rows.length,
																colIndex: 0,
															});
														}
													}
												};

												return (
													<td
														key={cell.id}
														className="border-r border-[#EAECF0] px-3 py-2 text-left align-top font-roboto font-normal text-primary-500"
													>
														{editCell?.rowIndex === rowIndex &&
														editCell?.colIndex === colIndex ? (
															<div className="flex items-start gap-2">
																<Field
																	name={getCellPath(rowIndex, colIndex)}
																	as="input"
																	type="text"
																	className={`w-full rounded px-2 py-1 focus:outline-none focus:ring-2 ${
																		error
																			? "border border-red-500 focus:ring-red-500"
																			: "border border-gray-300 focus:ring-blue-500"
																	}`}
																	autoFocus={
																		editCell?.rowIndex === rowIndex &&
																		editCell?.colIndex === colIndex
																	}
																	onBlur={() =>
																		handleSaveCell(rowIndex, colIndex)
																	}
																	onKeyDown={(e: React.KeyboardEvent) => {
																		handleKeyDown(e, rowIndex, colIndex);
																	}}
																/>
																<button
																	onClick={() =>
																		handleSaveCell(rowIndex, colIndex)
																	}
																>
																	<Icon icon="check" />
																</button>
															</div>
														) : (
															<div
																onClick={() =>
																	!disabled &&
																	setEditCell({ rowIndex, colIndex })
																}
																className={`min-h-[2rem] ${disabled ? "cursor-default" : "cursor-pointer"} rounded p-1 hover:bg-gray-50 ${
																	error ? "border border-red-500" : ""
																}`}
															>
																{cell.value ||
																	(!disabled && (
																		<span className="text-gray-400">
																			Click to edit
																		</span>
																	))}
															</div>
														)}
														{error && (
															<p className="mt-1 text-sm text-red-500">
																{error}
															</p>
														)}
													</td>
												);
											})}
											{!disabled && !isClientPreview && (
												<td className="px-3 py-2 text-center">
													<button
														onClick={() => remove(rowIndex)}
														disabled={disabled}
														className="text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50"
														title="Delete row"
													>
														<Tooltip label="Delete row (this cannot be undone)">
															<Icon
																style={{ width: "20px", height: "20px" }}
																icon="trash"
															/>
														</Tooltip>
													</button>
												</td>
											)}
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{!disabled && (
							<button
								onClick={addRow}
								disabled={disabled}
								className="mt-4 flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<span className="text-xl">+</span>
								Add Row
							</button>
						)}
					</div>
				);
			}}
		</FieldArray>
	);
};
